import { useEffect, useMemo, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

import { IconButton } from '@entur/button';
import { AlertIcon, QuestionIcon } from '@entur/icons';
import { Contrast } from '@entur/layout';
import { CollapsibleSideNavigation, SideNavigationGroup, SideNavigationItem } from '@entur/menu';
import { Tooltip } from '@entur/tooltip';
import logoSmal from 'assets/img/logo_smal.png';
import logo from 'assets/img/logo_white.png?w=72&format=webp';
import classNames from 'classnames/bind';
import { SearchableDropdown } from 'components/Dropdowns/SearchableDropdown';
import { Kbd, Plus } from 'components/Kbd';
import { DEPLOYMENT } from 'config';
import { KeyboardNavigationModal } from '../HelpModal/KeyboardNavigationModal';
import { getChartOfAccountsDropdownItems } from 'features/charts-of-accounts/utils';
import { OperatingMessagesModal } from 'features/operating-messages/components/OperatingMessagesModal';
import { useAuth } from 'hooks/context/useAuthContext';
import { useChartOfAccounts } from 'hooks/context/useChartOfAccounts';
import { useDrawer } from 'hooks/context/useDrawer';
import { useModal } from 'hooks/useModal';
import { useOptimisticSearchParams } from 'nuqs/adapters/react-router/v7';
import { useIntl } from 'react-intl';
import { Link, useLocation, useNavigate } from 'react-router';
import styles from './styles.module.scss';
import { getMenuGroupKey, getVisibleMenuGroups } from './menuConfig';
import UserMenu from './UserMenu';

interface NavBarProps {
  collapsed: boolean;
  updateCollapsed: (bool: boolean) => void;
}

const NavBar = ({ collapsed, updateCollapsed }: NavBarProps) => {
  const cx = classNames.bind(styles);
  const intl = useIntl();
  const location = useLocation();
  const searchParams = useOptimisticSearchParams();
  const navigate = useNavigate();
  const { hasPrivilege, internal, getToken, hasTokenExpired, getUser, isAuthenticated } = useAuth();
  const { close } = useDrawer();

  const {
    openModal: openOperatingMessagesModal,
    isModalOpen: isOperatingMessagesModalOpen,
    closeModal: closeOperatingMessagesModal,
  } = useModal();
  const { openModal: openHelpModal, isModalOpen: isHelpModalOpen, closeModal: closeHelpModal } = useModal();

  const { chartOfAccounts, updateChartOfAccounts, myChartOfAccountsList, error } = useChartOfAccounts();

  const activeSubKey = useMemo(() => {
    const url = location.pathname;
    if (url.includes('/')) {
      const urlKey = url.includes('chart-of-accounts') ? url.split('/')[3] : url.split('/')[1];
      return urlKey;
    }
  }, [location.pathname]);

  const activeMainKey = useMemo(() => getMenuGroupKey(activeSubKey), [activeSubKey]);

  const [menuTab, setMenuTab] = useState(activeMainKey);
  const isCollapsed = collapsed;

  useEffect(() => {
    close();
  }, [location.pathname, close]);

  useHotkeys(
    'mod+k',
    (event) => {
      event.preventDefault();
      if (isHelpModalOpen) {
        closeHelpModal();
      } else {
        openHelpModal();
      }
    },
    { enableOnFormTags: true },
    [isHelpModalOpen, openHelpModal, closeHelpModal]
  );

  const printEnvLabel = () => {
    const envString = DEPLOYMENT !== 'production' ? intl.formatMessage({ id: `versionsPage.${DEPLOYMENT}` }) : '';
    return (
      <a href="/versions" className={styles.envInfo}>
        {`${__APP_VERSION__ && __APP_VERSION__.split('-').slice(0, 1).join().split('.').slice(0, 3).join('.')} ${envString}`}
      </a>
    );
  };

  const changeMenuTab = (value: string) => {
    if (menuTab === value) {
      setMenuTab(null);
    } else {
      setMenuTab(value);
    }
  };

  const renderMenuGroups = () =>
    getVisibleMenuGroups({ hasPrivilege, internal }).map(({ key, labelId, Icon, items }) => (
      <SideNavigationGroup
        key={key}
        title={intl.formatMessage({ id: labelId })}
        icon={<Icon className={styles.icon} />}
        open={menuTab === key}
        onToggle={() => {
          changeMenuTab(key);
          updateCollapsed(false);
        }}
      >
        {items.map((item) => (
          <SideNavigationItem
            key={item.key}
            as={Link}
            to={item.path(chartOfAccounts?.id)}
            state={{ restoreCrumbs: true }}
            active={activeSubKey === item.key}
          >
            {intl.formatMessage({ id: item.labelId })}
          </SideNavigationItem>
        ))}
      </SideNavigationGroup>
    ));

  const renderKeyboardnavigation = () => {
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
    const shortcutText = isMac ? 'Cmd' : 'Ctrl';

    const shortcut = (
      <>
        <Kbd>{shortcutText}</Kbd> <Plus /> <Kbd>K</Kbd>
      </>
    );

    if (isCollapsed) {
      return (
        <div className={styles.helpSection}>
          <button
            onClick={openHelpModal}
            className={styles.helpButton}
            title={intl.formatMessage({ id: 'menu.shortcuts' }, { defaultMessage: 'Snarveier' })}
          >
            <QuestionIcon />
          </button>
        </div>
      );
    }

    return (
      <div className={styles.helpSection}>
        <button onClick={openHelpModal} className={styles.helpButton}>
          <span>{intl.formatMessage({ id: 'menu.shortcuts' }, { defaultMessage: 'Snarveier' })}</span>
          <span className={styles.shortcut}>{shortcut}</span>
        </button>
      </div>
    );
  };

  const renderEnvBanner = () => {
    if (!DEPLOYMENT || DEPLOYMENT === 'production') return null;
    return <div className={styles.envBanner} data-env={DEPLOYMENT} aria-hidden="true" />;
  };

  const renderTopNav = () => (
    <>
      <KeyboardNavigationModal isOpen={isHelpModalOpen} onDismiss={closeHelpModal} />
      {renderEnvBanner()}
      {isCollapsed ? (
        <>
          <Link to="/" state={{ restoreCrumbs: true }}>
            <img className={styles.logoSmall} src={logoSmal} alt="Entur logo" />
          </Link>
          <UserMenu collapsed={isCollapsed} />
        </>
      ) : (
        <>
          <div className={styles.navbarHeader}>
            <Link className={styles.cleosLink} to="/" state={{ restoreCrumbs: true }}>
              <img src={logo} alt="Entur logo" />
              <span>CLEOS</span>
            </Link>
            {printEnvLabel()}
            <Tooltip
              aria-hidden
              placement="bottom"
              content={intl.formatMessage({ id: 'operatingMessages.typePlural' })}
            >
              <IconButton
                onClick={openOperatingMessagesModal}
                className={styles.operatingMessageButton}
                aria-label={intl.formatMessage({ id: 'operatingMessages.typePlural' })}
              >
                <AlertIcon />
              </IconButton>
            </Tooltip>
          </div>
          <UserMenu collapsed={isCollapsed} />
          <OperatingMessagesModal isOpen={isOperatingMessagesModalOpen} onDismiss={closeOperatingMessagesModal} />
        </>
      )}
    </>
  );

  const renderChartOfAccountsDropdown = () => {
    const chartsOfAccountsMissing = myChartOfAccountsList?.length === 0 || error;
    const dropdownList = getChartOfAccountsDropdownItems(myChartOfAccountsList, 'id');

    if (!isCollapsed && myChartOfAccountsList?.length) {
      return (
        <SearchableDropdown
          className={styles.chartOfAccountsDropdown}
          items={dropdownList}
          value={chartOfAccounts?.id}
          variant={chartsOfAccountsMissing ? 'error' : 'info'}
          feedback={chartsOfAccountsMissing ? intl.formatMessage({ id: 'menu.chartOfAccountsError' }) : undefined}
          label={intl.formatMessage({ id: 'menu.chartOfAccounts' })}
          onChange={(item) => {
            if (!item) return;
            updateChartOfAccounts(item);

            const path = location.pathname.split('/').filter(Boolean);

            if (path[0] === 'chart-of-accounts' && path.length >= 3) {
              navigate(`/chart-of-accounts/${item}/${path[2]}?${searchParams}`);
            }
          }}
          clearable={false}
        />
      );
    }
  };

  if (!getToken() || hasTokenExpired()) return <div />;

  return (
    getUser() &&
    isAuthenticated() && (
      <div className={cx({ navbarContainer: true, collapsed: isCollapsed })}>
        <Contrast>
          <CollapsibleSideNavigation
            collapsed={isCollapsed}
            onCollapseToggle={(e) => {
              updateCollapsed(!collapsed);
              if (!e) {
                setMenuTab(activeMainKey);
              } else {
                setMenuTab(null);
              }
            }}
            className={styles.navbar}
          >
            {renderTopNav()}
            {renderChartOfAccountsDropdown()}
            {renderMenuGroups()}
            {renderKeyboardnavigation()}
          </CollapsibleSideNavigation>
        </Contrast>
      </div>
    )
  );
};

export default NavBar;
